import { apiClient } from './client';
import { PaginatedResponse } from '../types/pagination';
import { PersonalAppointmentStatus } from '../types/lead';

export type GoogleCalendarSyncStatus = 'NOT_CONNECTED' | 'SYNCED' | 'FAILED';

export interface PersonalAppointmentCounselorEntry {
  id: number;
  isPrimary: boolean;
  user: { id: number; firstName: string; lastName: string; email: string };
}

export interface PersonalAppointmentDetail {
  id: number;
  status: PersonalAppointmentStatus;
  appointmentDate: string | null;
  notes: string | null;
  createdAt: string;
  completedAt: string | null;
  completedBy: { id: number; firstName: string; lastName: string } | null;
  createdBy: { id: number; firstName: string; lastName: string };
  counselors: PersonalAppointmentCounselorEntry[];
  googleEventId: string | null;
  googleCalendarSyncStatus: GoogleCalendarSyncStatus | null;
  lead: {
    id: number;
    leadNumber: string;
    firstName: string;
    lastName: string;
    phone: string;
    email: string | null;
    leadSource: { id: number; name: string };
    assignedTo: { id: number; firstName: string; lastName: string } | null;
    branch: { id: number; name: string } | null;
    student: { id: number; studentNumber: string } | null;
  };
}

export interface ListPersonalAppointmentsParams {
  page: number;
  pageSize: number;
  status?: PersonalAppointmentStatus;
  branchId?: number;
}

export const listPersonalAppointments = async (
  params: ListPersonalAppointmentsParams,
): Promise<PaginatedResponse<PersonalAppointmentDetail>> => {
  const { data } = await apiClient.get('/personal-appointments', { params });
  return data;
};

export interface EligibleAppointmentCounselor {
  id: number;
  firstName: string;
  lastName: string;
}

/** "Assign Counselors" picker (Phase 77) — active Counselors in the
 * caller's own branch, backing the Convert-to-Personal-Appointment
 * dialog's multi-select. */
export const listEligibleAppointmentCounselors = async (): Promise<EligibleAppointmentCounselor[]> => {
  const { data } = await apiClient.get('/personal-appointments/eligible-counselors');
  return data.data;
};

export const createPersonalAppointment = async (payload: {
  leadId: number;
  appointmentDate?: string;
  notes?: string;
  counselorIds?: number[];
}): Promise<PersonalAppointmentDetail> => {
  const { data } = await apiClient.post('/personal-appointments', payload);
  return data.data;
};

export const getPersonalAppointment = async (id: number): Promise<PersonalAppointmentDetail> => {
  const { data } = await apiClient.get(`/personal-appointments/${id}`);
  return data.data;
};

export const updatePersonalAppointment = async (
  id: number,
  payload: {
    appointmentDate?: string | null;
    notes?: string | null;
    status?: 'SCHEDULED' | 'CANCELLED';
    counselorIds?: number[];
  },
): Promise<PersonalAppointmentDetail> => {
  const { data } = await apiClient.patch(`/personal-appointments/${id}`, payload);
  return data.data;
};

export const completePersonalAppointment = async (id: number): Promise<PersonalAppointmentDetail> => {
  const { data } = await apiClient.post(`/personal-appointments/${id}/complete`);
  return data.data;
};
